import React, { useState } from "react";
import { Trash2, ShoppingBag, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { CartLineControls, hasMissingDates } from "@/components/CartLineControls";
import { useApp, formatEUR } from "@/context/AppContext";
import { getProduct } from "@/data/products";
import { startCheckout } from "@/lib/api";
import { toast } from "sonner";

export function CartDrawer() {
  const { t, lang, cart, cartOpen, setCartOpen, removeFromCart } = useApp();
  const [loading, setLoading] = useState(false);

  const lines = cart.map((i) => ({ ...i, product: getProduct(i.id) })).filter((l) => l.product);
  const ttc = lines.reduce((s, l) => s + l.product.price * l.quantity, 0);
  const ht = ttc / 1.2;
  const tva = ttc - ht;
  const missing = hasMissingDates(lines);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      await startCheckout(cart);
    } catch (e) {
      toast.error(lang === "fr" ? "Erreur lors du paiement. Réessayez." : "Payment error. Please retry.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="text-left text-lg font-bold text-cea-navy">{t.cart.title}</SheetTitle>
        </SheetHeader>

        {lines.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center text-slate-500" data-testid="cart-empty">
            <ShoppingBag className="h-12 w-12 text-slate-300" />
            <p>{t.cart.empty}</p>
            <Button asChild variant="outline" onClick={() => setCartOpen(false)}>
              <Link to="/catalogue" data-testid="cart-goto-catalogue">{t.cta.viewCatalogue}</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="hide-scrollbar -mx-1 flex-1 space-y-4 overflow-y-auto px-1 py-4">
              {lines.map((l) => (
                <div key={l.id} className="flex gap-3 rounded-xl border border-slate-200 p-3" data-testid={`cart-line-${l.id}`}>
                  <img src={l.product.images[0]} alt={l.product.name[lang]} className="h-20 w-20 shrink-0 rounded-lg object-cover" />
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold leading-tight text-cea-ink">{l.product.name[lang]}</p>
                      <button onClick={() => removeFromCart(l.id)} data-testid={`cart-remove-${l.id}`} className="text-slate-400 transition-colors hover:text-red-500" aria-label={t.cart.remove}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">{formatEUR(l.product.price)}</p>
                    <div className="mt-auto flex flex-col gap-2 pt-2">
                      <CartLineControls line={l} prefix="cart" />
                      <p className="self-end text-sm font-bold text-cea-navy">{formatEUR(l.product.price * l.quantity)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-slate-200 pt-4">
              <div className="flex justify-between text-sm text-slate-600"><span>{t.cart.ht}</span><span data-testid="cart-ht">{formatEUR(ht)}</span></div>
              <div className="flex justify-between text-sm text-slate-600"><span>{t.cart.tva}</span><span data-testid="cart-tva">{formatEUR(tva)}</span></div>
              <div className="flex justify-between text-base font-bold text-cea-ink"><span>{t.cart.ttc}</span><span data-testid="cart-ttc">{formatEUR(ttc)}</span></div>
              {missing && <p className="text-xs font-medium text-amber-600" data-testid="cart-missing-dates">{t.dates.missing}</p>}
              <Button
                onClick={handleCheckout}
                disabled={loading || missing}
                data-testid="checkout-button"
                className="mt-2 w-full rounded-full bg-cea-accent py-6 text-base font-semibold text-white hover:bg-cea-navy"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t.cta.checkout}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
